<script setup lang="ts">
  import { Button } from '@/components/ui/button'
  import { useTranslation } from '@/composables/useTranslation'
  import { downloadBlob } from '@/lib/utils'
  import { DocumentsService, FormsService } from '@/types/generated'
  import { DownloadIcon } from 'lucide-vue-next'

  interface Props {
    pdfUrl: string
    filename?: string
    headers?: Record<string, string>
    class?: string
    documentUuid?: string
    requestToken?: string
  }

  const props = defineProps<Props>()
  const { t } = useTranslation({
    en: {
      download: 'Download'
    },
    de: {
      download: 'Herunterladen'
    }
  })

  const handleDownload = async () => {
    try {
      let downloadUrl = props.pdfUrl
      let filename = props.filename || 'document.pdf'

      // If we have a request token, use FormsService to get the completed document URL
      if (props.requestToken) {
        try {
          const response = await FormsService.formsPdfFormRetrieve({
            token: props.requestToken
          })
          if (
            response.documentProperties?.download &&
            typeof response.documentProperties.download === 'object' &&
            'url' in response.documentProperties.download
          ) {
            downloadUrl = (response.documentProperties.download as any).url
            filename = (response.documentProperties.download as any).filename || filename
          }
        } catch (error) {
          console.error('Error getting completed document URL:', error)
        }
      } else if (props.documentUuid && !downloadUrl) {
        try {
          const response = await DocumentsService.documentsRetrieve({
            uuid: props.documentUuid
          })
          if (response.url) {
            downloadUrl = response.url
            filename = response.filename || filename
          }
        } catch (error) {
          console.error('Error getting document URL:', error)
        }
      }

      const response = await fetch(downloadUrl, {
        headers: props.headers || {}
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.arrayBuffer()
      const blob = new Blob([data], { type: 'application/pdf' })
      downloadBlob(blob, filename || 'document.pdf')
    } catch (error) {
      console.error('Download failed:', error)
    }
  }
</script>

<template>
  <Button
    :class="['h-8', props.class || 'w-[99px]']"
    variant="secondary"
    @click="handleDownload"
  >
    <DownloadIcon class="mr-2 h-4 w-4" />
    {{ t('download') }}
  </Button>
</template>
